import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BarChart3,
  Building2,
  FileText,
  FolderOpen,
  LogOut,
  MonitorPlay,
  PenLine,
  Plus,
  Users,
  ShieldCheck,
  UserRound,
  Archive,
  Trash2,
  Loader2,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { BotonTema } from '../components/ui/BotonTema';
import { Card } from '../components/ui/Card';
import { EsqueletoTarjeta } from '../components/ui/Esqueleto';
import { Badge } from '../components/ui/Badge';
import type { BadgeVariant } from '../components/ui/Badge';
import { useAuthStore } from '../store/authStore';
import { useSurfaceTheme } from '../hooks/useSurfaceTheme';
import { apiFetch } from '../lib/api';
import { sesionApi, sesionKeys, type ProcesoResumen } from '../lib/sesion';
import { MiPlan } from '../components/MiPlan';

/** Etiqueta y color del estado del proceso, en el idioma del conductor. */
const ESTADOS: Record<string, { texto: string; variant: BadgeVariant }> = {
  borrador: { texto: 'Borrador', variant: 'neutral' },
  activo: { texto: 'Activo', variant: 'primary' },
  cerrado: { texto: 'Cerrado', variant: 'success' },
  archivado: { texto: 'Archivado', variant: 'neutral' },
};

/**
 * Procesos (/procesos) — la PORTADA del conductor. Lista todos los procesos
 * de su org (el superadmin ve todas, con la institución como badge) y desde
 * acá se navega a componer, conducir, ver el tablero o la propuesta. Es una
 * página real: el botón atrás del navegador vuelve a funcionar.
 */
export function ProcesosPage() {
  useSurfaceTheme('cabina', 'dark');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  const procesos = useQuery({
    queryKey: sesionKeys.procesos,
    queryFn: sesionApi.procesos,
    refetchInterval: 15_000,
  });

  async function onLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  const todos = procesos.data ?? [];
  const [filtro, setFiltro] = useState<'trabajo' | 'archivados' | 'todos'>('trabajo');
  const [busca, setBusca] = useState('');

  // Por defecto se ve lo que está EN TRABAJO. Los archivados existen pero no
  // compiten por la atención: ese es el punto de archivar.
  const lista = todos
    .filter((p) =>
      filtro === 'todos'
        ? true
        : filtro === 'archivados'
          ? p.estado === 'archivado'
          : p.estado !== 'archivado',
    )
    .filter((p) =>
      busca.trim()
        ? (p.nombre + ' ' + (p.problema_enunciado ?? '')).toLowerCase().includes(busca.toLowerCase())
        : true,
    );
  const cuenta = {
    trabajo: todos.filter((p) => p.estado !== 'archivado').length,
    archivados: todos.filter((p) => p.estado === 'archivado').length,
    todos: todos.length,
  };

  return (
    <div className="flex min-h-screen flex-col bg-surface-base">
      {/* Barra superior: logo, quién soy, salir */}
      <header className="flex items-center justify-between border-b border-border px-6 py-3">
        <div className="flex items-center gap-4">
          <img src="/brand/logonegativo.png" alt="ÁGORA" className="h-7 w-auto" />
          <span className="hidden font-sans text-sm text-content-muted sm:inline">
            Tus procesos · {user?.nombre ?? ''}
            {user?.org_nombre ? ` — ${user.org_nombre}` : ''}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* La portada es donde aterriza el superadmin: puerta al gestor acá
              también, no solo en la Cabina. */}
          {user?.rol === 'superadmin' && (
            <Link
              to="/admin"
              className="flex min-h-touch items-center gap-1.5 rounded-md px-2.5 font-sans text-sm font-medium text-content-muted transition-colors duration-fast hover:bg-surface-sunken hover:text-content"
            >
              <ShieldCheck className="h-4 w-4" aria-hidden="true" />
              Administración
            </Link>
          )}
          {(user?.rol === 'admin_org' || user?.rol === 'superadmin') && (
            <Link
              to="/organizacion"
              title="Mi organización: identidad, usuarios y asientos"
              className="flex min-h-touch items-center gap-1.5 rounded-md px-2.5 font-sans text-sm font-medium text-content-muted transition-colors duration-fast hover:bg-surface-sunken hover:text-content"
            >
              <Building2 className="h-4 w-4" aria-hidden="true" />
              Mi organización
            </Link>
          )}
          <Link
            to="/cuenta"
            title="Mi cuenta: perfil, plan y consumo"
            className="flex min-h-touch items-center gap-1.5 rounded-md px-2.5 font-sans text-sm font-medium text-content-muted transition-colors duration-fast hover:bg-surface-sunken hover:text-content"
          >
            <UserRound className="h-4 w-4" aria-hidden="true" />
            Mi cuenta
          </Link>
          <BotonTema />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void onLogout()}
            leadingIcon={<LogOut className="h-4 w-4" aria-hidden="true" />}
          >
            Salir
          </Button>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-4xl flex-1 flex-col gap-4 px-6 py-6">
        <MiPlan />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="flex items-center gap-2 font-display text-2xl font-semibold text-content">
            <FolderOpen className="h-6 w-6 text-primary" aria-hidden="true" />
            Procesos
          </h1>
          {/* Los participantes (miembro) aportan, no componen: el backend
              también lo rechaza (403), esto solo evita la puerta muerta. */}
          {(user?.rol === 'facilitador' || user?.rol === 'admin_org' || user?.rol === 'superadmin') && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate('/nuevo')}
              leadingIcon={<Plus className="h-4 w-4" aria-hidden="true" />}
            >
              Nuevo proceso
            </Button>
          )}
        </div>

        {/* Filtros: lo que está en trabajo primero; lo archivado, a un clic. */}
        {todos.length > 3 && (
          <div className="flex flex-wrap items-center gap-2">
            {(['trabajo', 'archivados', 'todos'] as const).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFiltro(f)}
                className={
                  filtro === f
                    ? 'rounded-full bg-navy px-3 py-1.5 font-sans text-xs font-semibold text-white'
                    : 'rounded-full border border-border px-3 py-1.5 font-sans text-xs font-medium text-content-muted hover:text-content'
                }
              >
                {f === 'trabajo' ? 'En trabajo' : f === 'archivados' ? 'Archivados' : 'Todos'}
                <span className="ml-1.5 opacity-70">{cuenta[f]}</span>
              </button>
            ))}
            <span className="flex-1" />
            <input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar por nombre o problema…"
              className="min-w-[200px] rounded-lg border border-border bg-surface-raised px-3 py-1.5 font-sans text-sm text-content"
              aria-label="Buscar procesos"
            />
          </div>
        )}

        {procesos.isLoading ? (
          <div className="flex flex-col gap-3">
            <EsqueletoTarjeta />
            <EsqueletoTarjeta />
            <EsqueletoTarjeta />
          </div>
        ) : procesos.isError ? (
          <p className="font-sans text-sm text-error">
            No se pudieron cargar los procesos. Revisá tu conexión e intentá de nuevo.
          </p>
        ) : lista.length === 0 ? (
          <SinProcesos />
        ) : (
          <ul className="flex flex-col gap-3">
            {lista.map((p) => (
              <li key={p.id}>
                <ProcesoCard proceso={p} />
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}

/* ------------------------------- Piezas -------------------------------- */

function ProcesoCard({ proceso }: { proceso: ProcesoResumen }) {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const [confirmando, setConfirmando] = useState(false);
  const [nombreEscrito, setNombreEscrito] = useState('');
  const puedeEliminar = user?.rol === 'admin_org' || user?.rol === 'superadmin';

  const invalidar = () => void qc.invalidateQueries({ queryKey: sesionKeys.procesos });
  const archivar = useMutation({
    mutationFn: () => apiFetch(`/procesos/${proceso.id}/archivar`, { method: 'POST' }),
    onSuccess: invalidar,
  });
  const eliminar = useMutation({
    mutationFn: () =>
      apiFetch<{ detail: string }>(`/procesos/${proceso.id}/eliminar`, {
        method: 'POST',
        json: { confirmar: nombreEscrito },
      }),
    onSuccess: (r) => {
      window.alert(r.detail);
      setConfirmando(false);
      invalidar();
    },
  });

  const estado = ESTADOS[proceso.estado] ?? { texto: proceso.estado, variant: 'neutral' as const };
  const activo = proceso.estado === 'activo';
  const borrador = proceso.estado === 'borrador';
  const cerrado = proceso.estado === 'cerrado';

  return (
    <Card>
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="font-display text-lg font-semibold text-content">{proceso.nombre}</h2>
          <Badge variant={estado.variant}>{estado.texto}</Badge>
          {/* org_nombre solo viaja para el superadmin: institución dueña. */}
          {proceso.org_nombre && (
            <span
              className="flex items-center gap-1 font-sans text-xs text-content-muted"
              title="Institución dueña del proceso"
            >
              <Building2 className="h-3.5 w-3.5" aria-hidden="true" />
              {proceso.org_nombre}
            </span>
          )}
        </div>

        {proceso.problema_enunciado && (
          <p className="font-sans text-sm text-content-muted">{proceso.problema_enunciado}</p>
        )}

        <div className="flex flex-wrap items-center gap-2">
          {activo && (
            <AccionLink
              to={`/cabina?proceso=${proceso.id}`}
              icono={MonitorPlay}
              principal
              titulo={`Conducir «${proceso.nombre}» en la Cabina`}
            >
              Conducir
            </AccionLink>
          )}
          {(borrador || activo) && (
            <AccionLink
              to={`/compositor/${proceso.id}`}
              icono={PenLine}
              titulo={`Abrir «${proceso.nombre}» en el compositor`}
            >
              Componer
            </AccionLink>
          )}
          {(activo || cerrado) && (
            <AccionLink
              to={`/participantes/${proceso.id}`}
              icono={Users}
              titulo={`Gestionar participantes de «${proceso.nombre}»`}
            >
              Participantes
            </AccionLink>
          )}
          {!borrador && (
            <>
              <AccionLink
                to={`/tablero/${proceso.id}`}
                icono={BarChart3}
                titulo={`Tablero de agregados de «${proceso.nombre}»`}
              >
                Tablero
              </AccionLink>
              <AccionLink
                to={`/propuesta/${proceso.id}`}
                icono={FileText}
                titulo={`Propuesta trazable de «${proceso.nombre}»`}
              >
                Propuesta
              </AccionLink>
            </>
          )}
        </div>
        {/* Archivar y eliminar viven aparte de las acciones de trabajo: son
            de otra naturaleza y mezclarlas invita al clic distraído. */}
        <div className="flex flex-wrap items-center gap-3 border-t border-border pt-3">
          <button
            type="button"
            onClick={() => archivar.mutate()}
            disabled={archivar.isPending}
            className="inline-flex items-center gap-1.5 font-sans text-xs font-medium text-content-muted transition hover:text-content"
          >
            <Archive className="h-3.5 w-3.5" aria-hidden="true" />
            {proceso.estado === 'archivado' ? 'Desarchivar' : 'Archivar'}
          </button>
          {puedeEliminar && (
            <button
              type="button"
              onClick={() => setConfirmando((v) => !v)}
              className="inline-flex items-center gap-1.5 font-sans text-xs font-medium text-error/80 transition hover:text-error"
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
              Eliminar
            </button>
          )}
          <span className="flex-1" />
          {proceso.estado === 'archivado' && (
            <span className="font-sans text-[11px] text-content-muted">
              Archivado: no aparece entre los activos, no se perdió nada.
            </span>
          )}
        </div>

        {confirmando && (
          <div className="rounded-lg border border-error/40 bg-surface-sunken p-3">
            <p className="font-sans text-xs text-content">
              Esto borra el proceso y <strong>toda su evidencia</strong>, y no se
              deshace. Si solo querés sacarlo de la vista, archivalo.
            </p>
            <p className="mt-1.5 font-sans text-xs text-content-muted">
              Para confirmar, escribí el nombre exacto: <strong className="text-content">{proceso.nombre}</strong>
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <input
                value={nombreEscrito}
                onChange={(e) => setNombreEscrito(e.target.value)}
                className="min-w-[220px] flex-1 rounded-lg border border-border bg-surface-base px-3 py-2 font-sans text-sm text-content"
                placeholder={proceso.nombre}
              />
              <button
                type="button"
                disabled={nombreEscrito.trim() !== proceso.nombre.trim() || eliminar.isPending}
                onClick={() => eliminar.mutate()}
                className="inline-flex min-h-touch items-center gap-1.5 rounded-lg bg-error px-4 py-2 font-sans text-sm font-bold text-white disabled:opacity-40"
              >
                {eliminar.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                )}
                Eliminar definitivamente
              </button>
              <button
                type="button"
                onClick={() => setConfirmando(false)}
                className="font-sans text-xs text-content-muted underline"
              >
                Cancelar
              </button>
            </div>
            {eliminar.isError && (
              <p className="mt-2 font-sans text-xs text-error">
                {(eliminar.error as { detail?: string })?.detail ?? 'No se pudo eliminar.'}
              </p>
            )}
          </div>
        )}

      </div>
    </Card>
  );
}

/**
 * Acción de la tarjeta: un Link con la piel de botón chico. La principal
 * (Conducir) usa el primario; el resto, el secundario sobrio.
 */
function AccionLink({
  to,
  icono: Icono,
  principal = false,
  titulo,
  children,
}: {
  to: string;
  icono: LucideIcon;
  principal?: boolean;
  titulo: string;
  children: string;
}) {
  const piel = principal
    ? 'bg-primary text-content-on-primary hover:bg-primary-700'
    : 'bg-surface-raised text-content border border-border-strong hover:bg-surface-sunken';
  return (
    <Link
      to={to}
      title={titulo}
      className={`inline-flex min-h-touch select-none items-center justify-center gap-2 rounded-md px-3 font-sans text-sm font-semibold transition-colors duration-fast focus-visible:outline-none focus-visible:shadow-focus ${piel}`}
    >
      <Icono className="h-4 w-4" aria-hidden="true" />
      {children}
    </Link>
  );
}

function SinProcesos() {
  const navigate = useNavigate();
  return (
    <Card>
      <div className="flex flex-col items-center gap-4 py-8 text-center">
        <FolderOpen className="h-10 w-10 text-content-muted" aria-hidden="true" />
        <div>
          <p className="font-display text-lg font-semibold text-content">
            Todavía no hay procesos
          </p>
          <p className="mt-1 font-sans text-sm text-content-muted">
            Empezá enunciando el problema — por ejemplo, «El agua llega solo tres
            días por semana en la zona 3» — y ÁGORA te propone el proceso.
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => navigate('/nuevo')}
          leadingIcon={<Plus className="h-5 w-5" aria-hidden="true" />}
        >
          Crear el primer proceso
        </Button>
      </div>
    </Card>
  );
}
